// /notary/category/$slug — Hub สำหรับ 8 หมวด Notary ตามมาตรฐานลูกค้า
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { NOTARY_ACTS, NOTARY_CATEGORIES, getNotaryActsByCategory, type NotaryCategory } from "@/data/notary-acts";
import { SEO_SITE, hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ShieldCheck, ArrowRight } from "lucide-react";
import HubLeadCTA from "@/components/HubLeadCTA";

export const Route = createFileRoute("/notary/category/$slug")({
  loader: ({ params }) => {
    const cat = NOTARY_CATEGORIES.find((c) => c.id === (params.slug as NotaryCategory));
    if (!cat) throw notFound();
    const acts = getNotaryActsByCategory(cat.id);
    if (acts.length === 0) throw notFound();
    return { cat, acts };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { cat, acts } = loaderData;
    const url = `${SEO_SITE}/notary/category/${cat.id}`;
    const title = `${cat.label} — บริการรับรองโดยทนาย Notary Public | NYC Legal`;
    const desc = `${cat.label} โดย Notarial Services Attorney ขึ้นทะเบียนสภาทนายความ ครอบคลุม ${acts.length} บริการย่อย ส่งทั่วประเทศ 77 จังหวัด — ส่งภาพเอกสารให้เราตรวจก่อน ฟรี`;
    const minPrice = Math.min(...acts.map(a => a.priceFromTh));
    const maxPrice = Math.max(...acts.map(a => a.priceFromTh));
    return {
      meta: [
        { title: ctaTitle(title, "/notary/category/$slug", "th") }, { name: "description", content: ctaDesc(desc, "/notary/category/$slug", "th")},
        { property: "og:title", content: ctaTitle(title, "/notary/category/$slug", "th")},
        { property: "og:description", content: ctaDesc(desc, "/notary/category/$slug", "th")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "th_TH" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(`/notary/category/${cat.id}`)],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "OfferCatalog",
            "@id": `${url}#catalog`,
            name: cat.label,
            url,
            inLanguage: "th",
            numberOfItems: acts.length,
            itemListElement: acts.map((a, i) => ({
              "@type": "Offer",
              position: i + 1,
              url: `${SEO_SITE}/notary/${a.slug}`,
              price: a.priceFromTh,
              priceCurrency: "THB",
              availability: "https://schema.org/InStock",
              eligibleRegion: { "@type": "Country", name: "TH" },
              itemOffered: {
                "@type": "Service",
                name: a.nameTh,
                alternateName: a.nameEn,
                serviceType: "Notarial Services Attorney",
                provider: { "@type": "LegalService", name: "NYC Language Institute", url: SEO_SITE },
              },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AggregateOffer",
            "@id": `${url}#offers`,
            url, priceCurrency: "THB",
            lowPrice: minPrice, highPrice: maxPrice, offerCount: acts.length,
            availability: "https://schema.org/InStock",
            eligibleRegion: { "@type": "Country", name: "TH" },
          }),
        },
      ],
    };
  },
  component: CategoryHub,
});

function CategoryHub() {
  const data = Route.useLoaderData() as { cat: typeof NOTARY_CATEGORIES[number]; acts: typeof NOTARY_ACTS };
  const { cat, acts } = data;
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep text-white">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
          <nav aria-label="Breadcrumb" className="mb-4 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/notary" className="hover:text-gold">Notary Public</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{cat.labelEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ShieldCheck className="h-3.5 w-3.5" /> Notarial Services Attorney
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            {cat.label}
          </h1>
          <p className="mt-3 text-base text-white/85 sm:text-lg" data-speakable="true">
            รวม {acts.length} บริการย่อยในหมวด <strong>{cat.labelEn}</strong> โดยทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร
            ขึ้นทะเบียนสภาทนายความ — เริ่ม ฿{Math.min(...acts.map(a=>a.priceFromTh)).toLocaleString()}
            ส่งบริการทั่วประเทศ 77 จังหวัด รับงานด่วน 1 วันทำการ
          </p>
        </div>
      </section>
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {acts.map((a) => (
            <Link
              key={a.slug}
              to="/notary/$act" params={{ act: a.slug }}
              className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:-translate-y-0.5 hover:border-gold hover:shadow-md transition"
            >
              <div className="text-[11px] font-bold uppercase tracking-widest text-gold">฿{a.priceFromTh.toLocaleString()} · {a.turnaround}</div>
              <h2 className="mt-1 font-display text-lg font-bold text-navy-deep">{a.nameTh}</h2>
              <p className="mt-1 text-xs text-slate-500">{a.nameEn}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-700 line-clamp-2">{a.useCases.slice(0, 2).join(" · ")}</p>
              <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-navy-deep group-hover:text-gold">
                ดูรายละเอียด <ArrowRight className="h-4 w-4" />
              </div>
            </Link>
          ))}
        </div>
      </section>
      <HubLeadCTA locale="th" service="Notary Public" />
    </main>
  );
}
